import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, CircularProgress } from '@material-ui/core'
import { Formik } from 'formik'
import lodash from 'lodash'
import moment from 'moment'
import { ROUTES } from 'routes'
import { EventsProxy } from 'services'
import { loadEventCircuitCategoryResults, updateChampionship } from 'state/modules/championships'
import {
  CHAMPIONSHIP_TYPES,
  LAP_PERCENTAGE_REQUIRED_MAX,
  LAP_PERCENTAGE_REQUIRED_MIN,
  MAX_DATE,
  MIN_DATE,
  STRING_EMPTY
} from 'utils/constants'
import { showSnackbarError, showSnackbarSuccess } from 'utils/snackbar'
import * as Yup from 'yup'

import { CategoriesSection } from '../CategoriesSection'
import { DescriptionSection } from '../DescriptionSection'
import { DistancesSection } from '../DistancesSection'
import { EventsSection } from '../EventsSection'
import { InformationSection } from '../InformationSection'
import { PointsSection } from '../PointsSection'
import { StateSection } from '../StateSection'

import { useStyles } from './EditForm.style'

const INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE = moment('01-01-2024')
const MAX_ALLOWED_DISCIPLINES = 3

const EditForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const { championship, eventCircuitCategoryResults } = useSelector((state) => state.championships)

  const [loading, setLoading] = useState(false)
  const [eventOptions, setEventOptions] = useState([])
  const [organizationOptions, setOrganizationOptions] = useState([])

  const buildFixtures = (fixtures, eventsCount) => {
    const normalizedFixtures = fixtures
      .map((fixture) => ({
        ...fixture,
        dateNumber: fixture.dateNumber || 0,
        points: Array.isArray(fixture.points) ? fixture.points : []
      }))
      .sort((a, b) => a.dateNumber - b.dateNumber)

    return Array.from({ length: eventsCount }, (_, index) => {
      const dateNumber = index + 1
      const existingFixture = normalizedFixtures.find(
        (fixture) => fixture.dateNumber === dateNumber
      )

      return (
        existingFixture || {
          dateNumber,
          points: []
        }
      )
    })
  }

  const fetchEventsCloneSuggestions = async () => {
    try {
      setLoading(true)

      const proxy = new EventsProxy()
      const data = await proxy.getChampionshipEventsCloneSuggestions()

      const results = data.map((x) => ({
        ...x,
        label: x.name,
        value: x.id
      }))

      const eventResults = results.sort((a, b) =>
        a.label.toLowerCase().trim().localeCompare(b.label.toLowerCase().trim())
      )

      const organizationResults = lodash
        .uniqBy(
          results.flatMap((x) =>
            x.organizations.map((o) => ({
              id: o.organizationId,
              label: o.organizationName,
              value: o.organizationId
            }))
          ),
          'value'
        )
        .sort((a, b) => a.label.toLowerCase().trim().localeCompare(b.label.toLowerCase().trim()))

      setOrganizationOptions(organizationResults)
      setEventOptions(eventResults)
    } catch (error) {
      showSnackbarError(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEventsCloneSuggestions()
  }, [])

  useEffect(() => {
    if (championship) {
      const eventIds = championship.events.map((e) => e.id)
      dispatch(loadEventCircuitCategoryResults(championship.id, eventIds))
    }
  }, [championship])

  const values = {
    id: championship.id || STRING_EMPTY,
    name: championship.name || STRING_EMPTY,
    description: championship.description || STRING_EMPTY,
    descriptionText: championship.descriptionText || STRING_EMPTY,
    countryId: championship.countryId || STRING_EMPTY,
    fromDate: moment(championship.from) || null,
    toDate: moment(championship.to) || null,
    disciplinesSelect: championship.disciplines.map((x) => ({ value: x.id, label: x.name })) || [],
    type: championship.type || STRING_EMPTY,
    eventsCount: championship.eventsCount || STRING_EMPTY,
    computedEventsCount: championship.computedEventsCount || STRING_EMPTY,
    lastEventAccreditationRequired: championship.lastEventAccreditationRequired,
    distanceAward: championship.distanceAward,
    organizationId: championship.organizationId,
    disputedEventsCount: championship.disputedEventsCount || 0,
    organizationName: championship.organization.name || STRING_EMPTY,
    eventName: STRING_EMPTY,
    organizationSelected: championship.organization,
    minLapPercentageRequired: championship.minLapPercentageRequired,
    useFixtureAttendancePoints: championship.fixtures.some((x) =>
      x.points.some((p) => p.position === 0)
    ),
    useFixturePositionPoints: championship.fixtures.some((x) =>
      x.points.some((p) => p.position !== 0)
    ),
    fixtures: buildFixtures(championship.fixtures, championship.eventsCount),

    doubleScoreEvents: championship.events.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.id]: !!curr.pointFactor
      }),
      {}
    ),
    excludedCategories:
      championship.excludedCategories
        ?.split(', ')
        .filter(Boolean)
        .reduce(
          (acc, x) => ({
            ...acc,
            [x]: { name: x, checked: true }
          }),
          {}
        ) ?? {},
    excludedDistances:
      championship.excludedDistances
        ?.split(', ')
        .filter(Boolean)
        .reduce(
          (acc, x) => ({
            ...acc,
            [x]: { name: x, checked: true }
          }),
          {}
        ) ?? {},
    assistancePoint: championship.championshipPoints.find((x) => x.position === 0)?.points || 0,
    championshipPoints: championship.championshipPoints.length
      ? championship.championshipPoints
          .filter((x) => x.position !== 0)
          .sort((a, b) => a.position - b.position)
      : [],
    events:
      championship.events.map((x) => ({
        ...x,
        label: x.name,
        value: x.id
      })) || []
  }

  const getChampionshipPoints = ({
    shouldUseFixturePoints,
    shouldKeepAllFixturePoints,
    useFixtureAttendancePoints,
    points
  }) => {
    if (!shouldUseFixturePoints) {
      return points
    }

    if (shouldKeepAllFixturePoints) {
      return []
    }

    if (useFixtureAttendancePoints) {
      return points.filter((p) => p.position !== 0)
    }

    return points.filter((p) => p.position === 0)
  }

  const getFixturePoints = ({
    shouldUseFixturePoints,
    shouldKeepAllFixturePoints,
    useFixtureAttendancePoints,
    points
  }) => {
    if (!shouldUseFixturePoints) return []

    if (shouldKeepAllFixturePoints) return points.filter((p) => p.points !== STRING_EMPTY)

    return points
      .filter((p) => p.points !== STRING_EMPTY)
      .filter((p) => (useFixtureAttendancePoints ? p.position === 0 : p.position !== 0))
  }

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string()
        .required('Debes ingresar el nombre del campeonato')
        .max(100, 'Máximo 100 caracteres')
        .trim(),
      description: Yup.string()
        .required('Debes ingresar una descripción del campeonato')
        .min(100, 'Mínimo 100 caracteres')
        .max(4000, 'Máximo 4000 caracteres')
        .trim(),
      eventsCount: Yup.number()
        .positive('El número de eventos debe ser positivo')
        .integer('El número de eventos debe ser un número entero')
        .required('Ingrese el número de eventos totales del campeonato')
        .test(
          'test-max-events',
          'Debe ser mayor o igual a la cantidad de eventos seleccionados',
          (value, { parent }) => value >= parent.events.length
        )
        .max(24, 'Máximo 24 eventos'),
      computedEventsCount: Yup.number()
        .positive('El número de eventos computables debe ser positivo')
        .integer('El número de eventos computables debe ser un número entero')
        .required('Ingrese el número de eventos computables del campeonato')
        .max(24, 'Máximo 24 eventos')
        .test(
          'less-than-eventsCount',
          'El número de eventos computables debe ser menor al total',
          (value, { parent }) => !parent.eventsCount || value <= parent.eventsCount
        ),
      minLapPercentageRequired: Yup.number()
        .positive('El porcentaje de vueltas requeridas para puntuar debe ser positivo')
        .integer('El porcentaje de vueltas requeridas para puntuar debe ser un número entero')
        .min(
          LAP_PERCENTAGE_REQUIRED_MIN,
          `El porcentaje de vueltas requeridas para puntuar debe ser mayor o igual a ${LAP_PERCENTAGE_REQUIRED_MIN}`
        )
        .max(
          LAP_PERCENTAGE_REQUIRED_MAX,
          `El porcentaje de vueltas requeridas para puntuar debe ser menor o igual a ${LAP_PERCENTAGE_REQUIRED_MAX}`
        ),
      disciplinesSelect: Yup.array()
        .required('Debes seleccionar al menos una disciplina')
        .min(1, 'Debes seleccionar al menos una disciplina')
        .max(MAX_ALLOWED_DISCIPLINES, 'Puede seleccionar hasta 3 disciplinas'),
      countryId: Yup.string().required('Debes seleccionar un país'),
      fromDate: Yup.mixed()
        .nullable()
        .required('Ingrese el año de inicio del campeonato')
        .test('valid-date', 'Formato inválida', (value) => moment(value).isValid())
        .test('range-date', 'Formato inválida', (value) =>
          moment(value).isBetween(moment(MIN_DATE), moment(MAX_DATE), 'YYYY-MM-DD')
        )
        .test('min-date', 'Podra escoger el año a partir de 2024', (value) =>
          moment(value).isSameOrAfter(INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE, 'year')
        )
        .when('toDate', {
          is: (toDate) => toDate && moment(toDate).isValid(),
          then: (schema) =>
            schema
              .test(
                'date-compare',
                'El año de inicio no puede ser mayor al de fin',
                (value, { parent }) => moment(value).isBefore(moment(parent.toDate))
              )
              .test(
                'championship-duration',
                'El campeonato puede durar hasta 2 años',
                (value, { parent }) => moment(parent.toDate).diff(value, 'year') <= 1
              )
        }),
      toDate: Yup.mixed()
        .nullable()
        .required('Ingrese el año de cierre del campeonato')
        .test('valid-date', 'Formato inválida', (value) => moment(value).isValid())
        .test('range-date', 'Formato inválida', (value) =>
          moment(value).isBetween(moment(MIN_DATE), moment(MAX_DATE), 'YYYY-MM-DD')
        )
        .test('min-date', 'Podra escoger el año a partir de 2024', (value) =>
          moment(value).isSameOrAfter(INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE, 'year')
        )
        .when('fromDate', {
          is: (fromDate) => fromDate && moment(fromDate).isValid(),
          then: (schema) =>
            schema.test(
              'date-compare',
              'El año de fin no puede ser mayor al de inicio',
              (value, { parent }) => moment(value).isAfter(moment(parent.fromDate))
            )
        }),
      events: Yup.array()
        .test(
          'test-max-length',
          'Ha superado el límite de eventos',
          (value, { parent }) => value.length <= parent.eventsCount
        )
        .when(['fromDate', 'toDate'], {
          is: (fromDate, toDate) =>
            fromDate && moment(fromDate).isValid() && toDate && moment(toDate).isValid(),
          then: (schema) =>
            schema.test(
              'date-range',
              'Al menos un evento esta fuera de la duración del campeonato',
              (value = [], { parent }) =>
                value.every((x) =>
                  moment(x.to).isBetween(parent.fromDate, parent.toDate, 'year', [])
                )
            )
        }),

      assistancePoint: Yup.number()
        .test(
          'is-positive',
          'Los puntos no pueden ser negativos (dejarlo en cero es vacio) ',
          (value) => !value || value >= 0
        )
        .integer('Los puntos deben ser un número entero')
        .max(999999, 'Los puntos podran ser hasta 6 dígitos'),

      championshipPoints: Yup.array().of(
        Yup.object().shape({
          points: Yup.number()
            .positive('Los puntos deben ser positivos')
            .integer('Los puntos deben ser un número entero')
            .required('Debes ingresar los puntos del campeonato')
            .max(999999, 'Los puntos podran ser hasta 6 dígitos')
        })
      ),
      excludedDistances: Yup.mixed().test(
        'allExcluded',
        'Debe marcar almenos una distancia',
        (value, { parent }) =>
          !value ||
          !Object.keys(value).length ||
          !parent.events.some((x) => x.hasResultsSheetFile) ||
          !eventCircuitCategoryResults?.distances
            ?.filter((x) => x.events.some((e) => parent.events.some((d) => d.id === e.eventId)))
            .every((x) => !!value[x.name] && value[x.name].checked)
      ),
      excludedCategories: Yup.mixed().test(
        'allExcluded',
        'Debe marcar almenos una categoría',
        (value = {}, { parent }) =>
          !value ||
          !Object.keys(value).length ||
          !parent.events.some((x) => x.hasResultsSheetFile) ||
          !eventCircuitCategoryResults?.categories
            ?.filter((x) => x.events.some((e) => parent.events.some((d) => d.id === e.eventId)))
            .filter((x) =>
              x.distances.some(
                (d) =>
                  !parent.excludedDistances ||
                  !parent.excludedDistances[d.name] ||
                  !parent.excludedDistances[d.name]?.checked
              )
            )
            .every((x) => !!value[x.name] && value[x.name].checked)
      )
    },
    ['fromDate', 'toDate', 'events', 'eventsCount']
  )

  const onSubmit = async ({
    type,
    name,
    description,
    eventsCount,
    computedEventsCount,
    lastEventAccreditationRequired,
    distanceAward,
    fromDate,
    toDate,
    countryId,
    disciplinesSelect,
    organizationId,
    excludedCategories,
    excludedDistances,
    assistancePoint,
    championshipPoints,
    doubleScoreEvents,
    minLapPercentageRequired,
    events,
    useFixtureAttendancePoints,
    useFixturePositionPoints,
    fixtures
  }) => {
    const prevChampionshipPoints = championshipPoints.filter((x) => x.position !== 0)

    const championshipPointsModel = prevChampionshipPoints.map((x, i) => ({
      ...x,
      position: i + 1,
      name: `${i + 1}`
    }))

    const points = assistancePoint
      ? [...championshipPointsModel, { points: assistancePoint, position: 0 }]
      : championshipPointsModel

    const shouldUseFixturePoints = useFixtureAttendancePoints || useFixturePositionPoints
    const shouldKeepAllFixturePoints = useFixtureAttendancePoints && useFixturePositionPoints

    const payload = {
      type,
      name: name.trim(),
      description: description.trim(),
      eventsCount,
      computedEventsCount,
      lastEventAccreditationRequired,
      minLapPercentageRequired,
      distanceAward,
      from: fromDate && fromDate.isValid() ? fromDate.format() : null,
      to: toDate && toDate.isValid() ? toDate.format() : null,
      countryId,
      disciplines: disciplinesSelect.map((x) => ({ id: x.value })),
      fixtures: fixtures.map((fixture) => ({
        dateNumber: fixture.dateNumber,
        points: getFixturePoints({
          shouldUseFixturePoints,
          shouldKeepAllFixturePoints,
          useFixtureAttendancePoints,
          points: fixture.points
        })
      })),
      events: events.map((x) => ({
        id: x.value,
        doublePointFactor: !!doubleScoreEvents && !!doubleScoreEvents[x.value]
      })),
      organizationId,
      excludedCategories: events.some((x) => x.hasResultsSheetFile)
        ? (
            Object.values(excludedCategories)
              ?.filter((x) => x.checked)
              .map((x) => x.name) ?? STRING_EMPTY
          ).join(', ')
        : STRING_EMPTY,
      excludedDistances:
        distanceAward && events.some((x) => x.hasResultsSheetFile)
          ? (
              Object.values(excludedDistances)
                ?.filter((x) => x.checked)
                .map((x) => x.name) ?? STRING_EMPTY
            ).join(', ')
          : STRING_EMPTY,
      championshipPoints: getChampionshipPoints({
        shouldUseFixturePoints,
        shouldKeepAllFixturePoints,
        useFixtureAttendancePoints,
        points
      })
    }

    const data = await dispatch(updateChampionship(championship.id, payload))

    if (data) {
      showSnackbarSuccess('El campeonato se ha actualizado correctamente.')
      history.push(`${ROUTES.CHAMPIONSHIPS.PROFILE}/${data.slugUrl}`)
    }
  }

  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize>
      {({ handleSubmit, isSubmitting, isValid, values, errors }) => (
        <form onSubmit={handleSubmit} className={classes.form}>
          <StateSection />

          <InformationSection />

          <DescriptionSection />

          <EventsSection
            organizationOptions={organizationOptions}
            eventOptions={eventOptions}
            loading={loading}
          />

          {values.distanceAward && values.events.some((e) => e.hasResultsSheetFile) && (
            <DistancesSection />
          )}

          {values.events.some((e) => e.hasResultsSheetFile) && <CategoriesSection />}

          {values.type === CHAMPIONSHIP_TYPES.POINTS.value && <PointsSection />}

          <Button
            variant='contained'
            color='primary'
            type='submit'
            className={classes.saveButton}
            disabled={isSubmitting || !isValid}
            endIcon={isSubmitting && <CircularProgress size={16} color='primary' />}>
            Guardar cambios
          </Button>
        </form>
      )}
    </Formik>
  )
}

export default EditForm
